import { useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage, type DeliveryOrderRowDto } from '@matix/api-client';
import { SHIPMENT_STATUSES, SHIPMENT_STATUS_LABELS, type ShipmentEvent, type ShipmentStatus, type ShipmentView, type ShippingProviderCode, type ShippingProviderOption } from '@matix/types';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { moneyPrecision } from '../../store/currency';
import { useCurrency } from '../print/useCurrency';
import { formatReceiptDateTime } from '../receipts/helpers';
import { shippingApi } from './api';
import { useInvalidateShipping, useShipmentForReceipt } from './hooks';
import { CourierMark, ShipmentChip } from './ShipmentChip';

/**
 * The shipping panel for one delivery order.
 *
 *   no shipment  → pick a courier (only the ones the admin activated)
 *   pending      → change courier, get a quote, create the shipment
 *   created …    → status, tracking, driver, history; refresh / cancel / label
 *   manual       → type the tracking number, move the status by hand
 *
 * Nothing here touches the order's money. An unpaid order ships as cash on
 * delivery (the courier collects); a paid one ships prepaid. The panel says
 * which before the shipment is created, because the driver's instructions
 * come from that flag.
 */
export function ShipmentDialog({
  order,
  providers,
  onClose,
}: {
  order: DeliveryOrderRowDto | null;
  providers: ShippingProviderOption[];
  onClose: () => void;
}): JSX.Element | null {
  const { t, lang } = useLang();
  const currency = useCurrency();
  const precision = moneyPrecision();
  const invalidate = useInvalidateShipping();
  const money = (n: number, cur?: string | null) => `${(Number.isFinite(n) ? n : 0).toFixed(Math.max(0, Math.min(8, precision)))} ${cur ?? currency.code}`;

  const receiptId = order?.id ?? null;
  const q = useShipmentForReceipt(receiptId);
  const [local, setLocal] = useState<ShipmentView | null>(null);
  const shipment = local ?? q.data?.current ?? null;
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState<ShippingProviderCode | ''>('');
  const [cod, setCod] = useState<boolean | null>(null);
  const [notes, setNotes] = useState('');
  const [quote, setQuote] = useState<{ cost: number; currency: string; customer_charge: number; breakdown: Array<{ label: string; amount: number }> } | null>(null);
  const [events, setEvents] = useState<ShipmentEvent[]>([]);
  const [tracking, setTracking] = useState('');
  const [manualStatus, setManualStatus] = useState<ShipmentStatus>('picked_up');

  useEffect(() => {
    setLocal(null);
    setQuote(null);
    setNotes('');
    setCod(null);
    setTracking('');
    setEvents([]);
  }, [receiptId]);

  useEffect(() => {
    if (shipment) setProvider(shipment.provider_code);
    else if (providers.length === 1) setProvider(providers[0]!.code);
  }, [shipment?.provider_code, providers.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!shipment || shipment.status === 'pending') return;
    let cancelled = false;
    shippingApi
      .events(shipment.id)
      .then((e) => {
        if (!cancelled) setEvents(e);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [shipment?.id, shipment?.status, shipment?.last_status_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = useMemo(() => providers.find((p) => p.code === provider) ?? null, [providers, provider]);
  const providerName = (p: ShippingProviderOption | null) => (p ? (lang === 'ar' ? p.name_ar : p.name) : '');

  if (!order) return null;

  async function run(fn: () => Promise<ShipmentView | void>, okMessage?: string): Promise<void> {
    setBusy(true);
    try {
      const v = await fn();
      if (v) setLocal(v);
      invalidate();
      if (okMessage) toast(okMessage, 'success');
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  const isCod = cod ?? shipment?.is_cod ?? !order.is_paid;
  const status = shipment?.status ?? null;
  const showCreate = shipment?.status === 'pending';
  const tracked = shipment && shipment.status !== 'pending';

  return (
    <div className="cc-modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="cc-modal ship-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cc-drawer-head">
          <div>
            <div className="cc-drawer-title">
              {t({ en: 'Shipping', ar: 'الشحن' })} · #{order.entry_no ?? order.id}
            </div>
            <div className="cc-drawer-sub">
              {(lang === 'ar' ? order.customer_name_ar || order.customer_name : order.customer_name) ?? ''}
              {order.customer_phone ? ` · ${order.customer_phone}` : ''}
            </div>
          </div>
          <button type="button" className="cc-drawer-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="ship-body">
          {/* ── No shipment yet: pick a courier ─────────────────────────────── */}
          {!shipment && (
            <>
              <div className="ship-section-title">{t({ en: 'Shipping company', ar: 'شركة الشحن' })}</div>
              <div className="ship-providers">
                {providers.map((p) => (
                  <button key={p.code} type="button" className={`ship-provider ${provider === p.code ? 'ship-provider--on' : ''}`} onClick={() => setProvider(p.code)}>
                    <CourierMark code={p.code} name={providerName(p)} />
                    <span className="ship-provider-name">{providerName(p)}</span>
                    {!p.contract_verified && <span className="ship-provider-note">{t({ en: 'manual', ar: 'يدوي' })}</span>}
                  </button>
                ))}
              </div>
              <button type="button" className="cc-send" disabled={busy || !provider} onClick={() => void run(() => shippingApi.assign(order.id, provider as ShippingProviderCode))}>
                {t({ en: `Use ${providerName(selected) || '…'}`, ar: `استخدام ${providerName(selected) || '…'}` })}
              </button>
              <p className="cc-modal-foot">
                {t({
                  en: 'Choosing a company does not send anything yet. The shipment is created on the next step, after the cost is known.',
                  ar: 'اختيار الشركة لا يرسل شيئًا بعد. تُنشأ الشحنة في الخطوة التالية بعد معرفة التكلفة.',
                })}
              </p>
            </>
          )}

          {/* ── Pending: change courier, quote, create ──────────────────────── */}
          {showCreate && shipment && (
            <>
              <div className="ship-section-title">{t({ en: 'Shipping company', ar: 'شركة الشحن' })}</div>
              <div className="ship-providers">
                {providers.map((p) => (
                  <button
                    key={p.code}
                    type="button"
                    className={`ship-provider ${shipment.provider_code === p.code ? 'ship-provider--on' : ''}`}
                    disabled={busy || !shipment.can.change_provider}
                    onClick={() => {
                      if (p.code === shipment.provider_code) return;
                      setQuote(null);
                      void run(() => shippingApi.changeProvider(shipment.id, p.code));
                    }}
                  >
                    <CourierMark code={p.code} name={providerName(p)} />
                    <span className="ship-provider-name">{providerName(p)}</span>
                  </button>
                ))}
              </div>

              {shipment.last_error && <div className="cc-warn">{shipment.last_error}</div>}

              <div className="ship-grid">
                <div className="ship-kv">
                  <span>{t({ en: 'Order total', ar: 'إجمالي الطلب' })}</span>
                  <strong>{money(order.net_value)}</strong>
                </div>
                <div className="ship-kv">
                  <span>{t({ en: 'Customer delivery charge', ar: 'رسوم التوصيل على العميل' })}</span>
                  <strong>{money(quote?.customer_charge ?? shipment.customer_charge, shipment.currency)}</strong>
                </div>
                <div className="ship-kv">
                  <span>{t({ en: 'Courier cost', ar: 'تكلفة شركة الشحن' })}</span>
                  <strong>
                    {quote ? money(quote.cost, quote.currency) : shipment.quoted_cost != null ? money(shipment.quoted_cost, shipment.currency) : '—'}
                  </strong>
                </div>
              </div>
              {quote && quote.breakdown.length > 0 && (
                <div className="ship-breakdown">
                  {quote.breakdown.map((b) => (
                    <span key={b.label}>
                      {b.label}: {money(b.amount, quote.currency)}
                    </span>
                  ))}
                </div>
              )}

              {selected?.cod_enabled !== false && (
                <label className="ship-check">
                  <input type="checkbox" checked={isCod} disabled={busy || order.is_paid} onChange={(e) => setCod(e.target.checked)} />
                  <span>
                    {order.is_paid
                      ? t({ en: 'Order is paid — ships prepaid', ar: 'الطلب مدفوع — يُشحن مدفوعًا' })
                      : t({ en: `Cash on delivery — driver collects ${money(order.net_value)}`, ar: `الدفع عند الاستلام — يحصّل السائق ${money(order.net_value)}` })}
                  </span>
                </label>
              )}
              {selected && !selected.cod_enabled && !order.is_paid && (
                <div className="cc-warn">
                  {t({ en: `${providerName(selected)} does not collect cash. Take the payment first, or pick another company.`, ar: `${providerName(selected)} لا تحصّل نقدًا. حصّل الدفع أولًا أو اختر شركة أخرى.` })}
                </div>
              )}

              <label className="cc-field">
                <span>{t({ en: 'Note for the courier (optional)', ar: 'ملاحظة لشركة الشحن (اختياري)' })}</span>
                <input value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} />
              </label>

              <div className="cc-card-actions">
                {shipment.can.quote && (
                  <button
                    type="button"
                    className="cc-btn"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        const r = await shippingApi.quote(shipment.id);
                        setQuote({ cost: r.cost, currency: r.currency, customer_charge: r.customer_charge, breakdown: r.breakdown });
                        return r.shipment;
                      })
                    }
                  >
                    {t({ en: 'Get cost', ar: 'معرفة التكلفة' })}
                  </button>
                )}
                <button
                  type="button"
                  className="cc-btn cc-btn--primary"
                  disabled={busy || (selected != null && !selected.cod_enabled && isCod)}
                  onClick={() => void run(() => shippingApi.create(shipment.id, { cod: isCod, notes: notes.trim() || null }), t({ en: 'Shipment created', ar: 'تم إنشاء الشحنة' }))}
                >
                  {selected?.capabilities.manual ? t({ en: 'Hand over', ar: 'تسليم' }) : t({ en: 'Create shipment', ar: 'إنشاء الشحنة' })}
                </button>
                <button type="button" className="cc-btn" disabled={busy} onClick={() => void run(() => shippingApi.cancel(shipment.id, null))}>
                  {t({ en: 'Remove', ar: 'إزالة' })}
                </button>
              </div>
            </>
          )}

          {/* ── Created and beyond ──────────────────────────────────────────── */}
          {tracked && shipment && (
            <>
              <div className="ship-head">
                <CourierMark code={shipment.provider_code} name={lang === 'ar' ? shipment.provider_name_ar : shipment.provider_name} size={36} />
                <div className="ship-head-text">
                  <div className="ship-head-name">{lang === 'ar' ? shipment.provider_name_ar : shipment.provider_name}</div>
                  <ShipmentChip shipment={shipment} />
                  {shipment.provider_status && <div className="ship-muted">{shipment.provider_status}</div>}
                </div>
              </div>

              <div className="ship-grid">
                <div className="ship-kv">
                  <span>{t({ en: 'Tracking number', ar: 'رقم التتبع' })}</span>
                  <strong>{shipment.tracking_number ?? '—'}</strong>
                </div>
                <div className="ship-kv">
                  <span>{t({ en: 'Driver', ar: 'السائق' })}</span>
                  <strong>{[shipment.driver_name, shipment.driver_phone].filter(Boolean).join(' · ') || '—'}</strong>
                </div>
                <div className="ship-kv">
                  <span>{t({ en: 'Customer charge', ar: 'رسوم العميل' })}</span>
                  <strong>{money(shipment.customer_charge, shipment.currency)}</strong>
                </div>
                <div className="ship-kv">
                  <span>{t({ en: 'Courier cost', ar: 'تكلفة الشحن' })}</span>
                  <strong>{shipment.actual_cost != null ? money(shipment.actual_cost, shipment.currency) : '—'}</strong>
                </div>
                <div className="ship-kv">
                  <span>{t({ en: 'Collect on delivery', ar: 'التحصيل عند التسليم' })}</span>
                  <strong>{shipment.is_cod ? money(shipment.cod_amount ?? 0, shipment.currency) : t({ en: 'Prepaid', ar: 'مدفوع مسبقًا' })}</strong>
                </div>
                <div className="ship-kv">
                  <span>{t({ en: 'Last update', ar: 'آخر تحديث' })}</span>
                  <strong>{shipment.last_status_at ? formatReceiptDateTime(shipment.last_status_at) : '—'}</strong>
                </div>
              </div>
              {shipment.last_error && <div className="cc-warn">{shipment.last_error}</div>}

              <div className="cc-card-actions">
                {shipment.tracking_url && (
                  <a className="cc-btn" href={shipment.tracking_url} target="_blank" rel="noreferrer">
                    {t({ en: 'Track online', ar: 'تتبع عبر الإنترنت' })}
                  </a>
                )}
                {shipment.can.refresh && (
                  <button type="button" className="cc-btn" disabled={busy} onClick={() => void run(() => shippingApi.refresh(shipment.id))}>
                    {t({ en: 'Refresh', ar: 'تحديث' })}
                  </button>
                )}
                {shipment.can.label && (
                  <button
                    type="button"
                    className="cc-btn"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        const l = await shippingApi.label(shipment.id);
                        if (l.url) window.open(l.url, '_blank', 'noopener');
                        else if (l.base64) window.open(`data:${l.content_type ?? 'application/pdf'};base64,${l.base64}`, '_blank', 'noopener');
                        else toast(t({ en: 'No label is available for this shipment.', ar: 'لا يوجد ملصق لهذه الشحنة.' }), 'warning');
                      })
                    }
                  >
                    {t({ en: 'Label / AWB', ar: 'الملصق / البوليصة' })}
                  </button>
                )}
                {shipment.can.cancel && (
                  <button type="button" className="cc-btn" disabled={busy} onClick={() => void run(() => shippingApi.cancel(shipment.id, null), t({ en: 'Shipment cancelled', ar: 'تم إلغاء الشحنة' }))}>
                    {t({ en: 'Cancel shipment', ar: 'إلغاء الشحنة' })}
                  </button>
                )}
              </div>

              {shipment.can.manual && (
                <div className="ship-manual">
                  <div className="ship-section-title">{t({ en: 'Record by hand', ar: 'تسجيل يدوي' })}</div>
                  <div className="ship-manual-row">
                    <input
                      className="cc-input"
                      placeholder={t({ en: 'Tracking number from the courier', ar: 'رقم التتبع من شركة الشحن' })}
                      value={tracking}
                      onChange={(e) => setTracking(e.target.value)}
                    />
                    <button type="button" className="cc-btn" disabled={busy || !tracking.trim()} onClick={() => void run(() => shippingApi.manual(shipment.id, { tracking_number: tracking.trim() }), t({ en: 'Saved', ar: 'تم الحفظ' }))}>
                      {t({ en: 'Save', ar: 'حفظ' })}
                    </button>
                  </div>
                  <div className="ship-manual-row">
                    <select className="cc-select" value={manualStatus} onChange={(e) => setManualStatus(e.target.value as ShipmentStatus)}>
                      {SHIPMENT_STATUSES.filter((s) => s !== 'pending' && s !== 'created').map((s) => (
                        <option key={s} value={s}>
                          {SHIPMENT_STATUS_LABELS[s][lang]}
                        </option>
                      ))}
                    </select>
                    <button type="button" className="cc-btn" disabled={busy} onClick={() => void run(() => shippingApi.setStatus(shipment.id, manualStatus, null))}>
                      {t({ en: 'Set status', ar: 'تعيين الحالة' })}
                    </button>
                  </div>
                </div>
              )}

              {events.length > 0 && (
                <>
                  <div className="ship-section-title">{t({ en: 'History', ar: 'السجل' })}</div>
                  <ul className="ship-events">
                    {events
                      .slice()
                      .reverse()
                      .map((e) => (
                        <li key={e.id}>
                          <span className="ship-muted">{formatReceiptDateTime(e.event_at)}</span>
                          <span>{SHIPMENT_STATUS_LABELS[e.status][lang]}</span>
                          {(e.description || e.location) && <span className="ship-muted">{[e.description, e.location].filter(Boolean).join(' · ')}</span>}
                        </li>
                      ))}
                  </ul>
                </>
              )}
            </>
          )}

          {q.isLoading && !shipment && <div className="ship-muted">{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>}
          {status && q.data?.history && q.data.history.length > 0 && (
            <div className="ship-muted">
              {t({ en: `${q.data.history.length} earlier shipment(s) on this order.`, ar: `${q.data.history.length} شحنة سابقة على هذا الطلب.` })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
